import {
  buildBlockDefaults,
  DEFAULT_FONT_FAMILY,
  DEFAULT_THEME_NEUTRAL,
  DEFAULT_THEME_PRIMARY,
} from '~/composables/useBuilderMeta'
import type { BusinessType } from '~/types/onboarding'
import type { Block, LocalizedText, Page, SiteSchema } from '~/types/site'

export function deepClone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T
}

export function block(component: string, enabled: boolean, propsOverride: Record<string, unknown> = {}): Block {
  return {
    component,
    enabled,
    props: {
      ...buildBlockDefaults(component),
      ...propsOverride,
    },
  }
}

export function page(
  title: LocalizedText,
  slug: string,
  enabled: boolean,
  blocks: Block[],
): Page {
  return { title, slug, enabled, blocks }
}

function baseMenuItems(includeTeam: boolean): Array<{ label: LocalizedText; to: string }> {
  const items: Array<{ label: LocalizedText; to: string }> = [
    { label: { en: 'Home', fa: 'خانه' }, to: '/' },
    { label: { en: 'Services', fa: 'خدمات' }, to: '/services' },
    { label: { en: 'Appointment', fa: 'نوبت‌دهی' }, to: '/booking' },
  ]
  if (includeTeam) {
    items.push({ label: { en: 'Medical team', fa: 'تیم پزشکی' }, to: '/team' })
  }
  items.push(
    { label: { en: 'Blog', fa: 'آموزش بیماران' }, to: '/blog' },
    { label: { en: 'About', fa: 'درباره ما' }, to: '/about' },
    { label: { en: 'Contact', fa: 'تماس' }, to: '/contact' },
  )
  return items
}

function sharedLayout(includeTeam: boolean): SiteSchema['layout'] {
  return {
    blocks: [
      block('BlockHeader', true, {
        logoText: { en: 'My Clinic', fa: 'مطب من' },
        logoImage: '',
        menuItems: baseMenuItems(includeTeam),
      }),
      block('BlockBanner', true, {
        icon: 'i-lucide-megaphone',
        title: {
          en: 'Book your appointment online — limited slots available.',
          fa: 'نوبت خود را آنلاین رزرو کنید — ظرفیت محدود.',
        },
        ctaLabel: { en: 'Book now', fa: 'رزرو نوبت' },
        ctaTo: '/booking',
        color: 'primary',
        ctaColor: 'neutral',
      }),
      block('BlockFooter', true, {
        variant: 'simple',
        phone: { en: 'Clinic phone: ', fa: 'تلفن مطب: ' },
        address: { en: 'Clinic address', fa: 'آدرس مطب' },
        logoText: { en: 'My Clinic', fa: 'مطب من' },
        logoImage: '',
        creatorLabel: { en: 'Made by AradCore', fa: 'ساخته شده توسط AradCore' },
        creatorUrl: 'https://aradcore.com',
        tagline: {
          en: 'Modern medical website built with AradCore.',
          fa: 'وب‌سایت پزشکی مدرن ساخته‌شده با AradCore.',
        },
        copyright: {
          en: '© All rights reserved.',
          fa: '© تمامی حقوق محفوظ است.',
        },
        columns: [],
        socialLinks: [],
      }),
    ],
  }
}

function homeBlocks(opts: {
  teamEnabled: boolean
  appointmentExtras?: Record<string, unknown>
}): Block[] {
  return [
    block('BlockLandingHero', true, {
      headlineImages: { en: [] as string[], fa: [] as string[] },
      bannerAutoplay: false,
      bannerAutoplayDelay: 5000,
      bannerLoop: false,
      title: {
        en: 'Expert care for your health',
        fa: 'مراقبت تخصصی برای سلامت شما',
      },
      description: {
        en: 'Book an appointment online and get personalized medical care.',
        fa: 'نوبت خود را آنلاین رزرو کنید و مراقبت پزشکی شخصی‌سازی‌شده دریافت کنید.',
      },
      primaryButton: {
        label: { en: 'Online appointment', fa: 'نوبت‌دهی اینترنتی' },
        to: '/booking#booking-wizard',
      },
      secondaryLinks: [],
      socialLinks: [],
    }),
    block('BlockServices', true),
    block('BlockAppointment', true, {
      buttonTo: '/booking#booking-wizard',
      ...(opts.appointmentExtras ?? {}),
    }),
    block('BlockTeam', opts.teamEnabled),
    block('BlockGallery', false),
    block('BlockTestimonials', false),
    block('BlockInsurance', false),
    block('BlockBlogList', true),
    block('BlockFaq', true),
    block('BlockProducts', false),
  ]
}

function aboutBlocks(doctorIntroEnabled: boolean): Block[] {
  return [
    block('BlockDoctorIntro', doctorIntroEnabled),
    block('BlockHighlights', true),
    block('BlockCredentials', true),
    block('BlockReasons', true),
    block('BlockSpecialties', true),
    block('BlockCtaBanner', true),
  ]
}

export function buildBaseSchema(type: BusinessType): SiteSchema {
  const isClinic = type === 'clinic'
  return {
    schemaVersion: 1,
    i18n: {
      enabled: true,
      locales: ['fa', 'en'],
      defaultLocale: 'fa',
    },
    title: {
      en: isClinic ? 'My Clinic' : 'My Practice',
      fa: isClinic ? 'کلینیک من' : 'مطب من',
    },
    siteSettings: {
      themePrimary: DEFAULT_THEME_PRIMARY,
      themeNeutral: DEFAULT_THEME_NEUTRAL,
      fontFamily: DEFAULT_FONT_FAMILY,
      logoUrl: '',
      favicon: '',
      description: { fa: '', en: '' },
    },
    layout: sharedLayout(isClinic),
    pages: {
      home: page(
        { en: 'Home', fa: 'صفحه اصلی' },
        '/',
        true,
        homeBlocks({
          teamEnabled: isClinic,
          appointmentExtras: isClinic
            ? {
                doctorSelectLabel: { en: 'Choose a doctor', fa: 'انتخاب پزشک' },
                doctorsLink: '/team',
              }
            : {},
        }),
      ),
      services: page(
        { en: 'Services', fa: 'خدمات' },
        '/services',
        true,
        [block('BlockServices', true)],
      ),
      booking: page(
        { en: 'Appointment', fa: 'نوبت‌دهی' },
        '/booking',
        true,
        [
          block('BlockAppointment', true, {
            buttonTo: '#booking-wizard',
            ...(isClinic
              ? {
                  doctorSelectLabel: { en: 'Choose a doctor', fa: 'انتخاب پزشک' },
                  doctorsLink: '/team',
                }
              : {}),
          }),
          block('BlockBookingWizard', true),
        ],
      ),
      blog: page(
        { en: 'Patient education', fa: 'آموزش بیماران' },
        '/blog',
        true,
        [block('BlockBlogList', true)],
      ),
      contact: page(
        { en: 'Contact', fa: 'تماس' },
        '/contact',
        true,
        [block('BlockContact', true)],
      ),
      about: page(
        { en: 'About', fa: 'درباره ما' },
        '/about',
        true,
        aboutBlocks(!isClinic),
      ),
      team: page(
        { en: 'Medical team', fa: 'تیم پزشکی' },
        '/team',
        isClinic,
        [block('BlockTeam', isClinic)],
      ),
      shop: page(
        { en: 'Shop', fa: 'فروشگاه' },
        '/shop',
        false,
        [block('BlockProducts', false)],
      ),
      pricing: page(
        { en: 'Pricing', fa: 'تعرفه‌ها' },
        '/pricing',
        false,
        [block('BlockProducts', false)],
      ),
    },
  }
}
